import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { getSchoolList, getSchoolById, addSchool, updateSchool, deleteSchool, searchSchoolByName } from '@/api/school'
import { getTeacherSubjectCategories } from '@/api/teacherapi/subjectSetting'
import { getTeacherCollegeList } from '@/api/teacherapi/collegesAndClass'
import { applySchoolBranding } from '@/router'

/**
 * 学校管理状态管理 Store (Pinia)
 * 使用 Composition API 风格
 */
export const useSchoolStore = defineStore('school', () => {
  // 状态 (State)
  const schoolList = ref([])
  const currentSchool = ref(null)
  const loading = ref(false)
  const error = ref(null)
  const searchResults = ref([])
  const lastFetchTime = ref(null) // 最后获取数据的时间
  const cacheExpireTime = 5 * 60 * 1000 // 缓存过期时间：5分钟
  const categoryList = ref([])
  const collegeList = ref([])

  // 计算属性 (Getters)
  const getAllSchools = computed(() => schoolList.value)
  const getCurrentSchool = computed(() => currentSchool.value)
  const isLoading = computed(() => loading.value)
  const getError = computed(() => error.value)
  const getSearchResults = computed(() => searchResults.value)
  const getCategoryList = computed(() => categoryList.value)
  const getCollegeList = computed(() => collegeList.value)
  
  // 获取学校总数
  const schoolCount = computed(() => schoolList.value.count)
  
  // 根据ID查找学校
  const findSchoolById = computed(() => {
    return (id) => schoolList.value.find(school => school.id === id)
  })

  // 辅助方法
  /**
   * 检查缓存是否有效
   */
  const isCacheValid = () => {
    if (!lastFetchTime.value) return false
    const now = Date.now()
    return (now - lastFetchTime.value) < cacheExpireTime
  }

  /**
   * 初始化store，加载本地存储的数据
   */
  const initStore = () => {
    try {
      // 尝试从本地存储加载当前学校信息
      const cachedCurrentSchool = localStorage.getItem('currentSchool')
      if (cachedCurrentSchool) {
        currentSchool.value = JSON.parse(cachedCurrentSchool)
        console.log('Store初始化：从本地存储加载当前学校:', currentSchool.value)
      }
      const cachedCategories = localStorage.getItem('categoryList')
      const cachedColleges = localStorage.getItem('collegeList')
      if (cachedCategories) {
        categoryList.value = JSON.parse(cachedCategories)
      }
      if (cachedColleges) {
        collegeList.value = JSON.parse(cachedColleges)
      }
    } catch (error) {
      console.error('Store初始化失败:', error)
    }
  }

  /**
   * 从本地存储加载数据
   */
  const loadFromLocalStorage = () => {
    try {
      const cachedData = localStorage.getItem('schoolList')
      const cachedTime = localStorage.getItem('schoolListFetchTime')
      const cachedCurrentSchool = localStorage.getItem('currentSchool')
      const cachedCategories = localStorage.getItem('categoryList')
      const cachedColleges = localStorage.getItem('collegeList')
      
      if (cachedData && cachedTime) {
        const data = JSON.parse(cachedData)
        const time = parseInt(cachedTime)
        
        // 检查缓存是否过期
        if (Date.now() - time < cacheExpireTime) {
          schoolList.value = data
          lastFetchTime.value = time
          console.log('从本地存储加载学校列表:', data.length, '条记录')
          
          // 加载当前学校信息
          if (cachedCurrentSchool) {
            currentSchool.value = JSON.parse(cachedCurrentSchool)
            console.log('从本地存储加载当前学校:', currentSchool.value)
          }
          if (cachedCategories) {
            categoryList.value = JSON.parse(cachedCategories)
          }
          if (cachedColleges) {
            collegeList.value = JSON.parse(cachedColleges)
          }
          
          return true
        }
      }
    } catch (error) {
      console.error('从本地存储加载数据失败:', error)
    }
    return false
  }

  /**
   * 保存数据到本地存储
   */
  const saveToLocalStorage = (data) => {
    try {
      const now = Date.now()
      localStorage.setItem('schoolList', JSON.stringify(data))
      localStorage.setItem('schoolListFetchTime', now.toString())
      lastFetchTime.value = now
      console.log('学校列表已保存到本地存储')
    } catch (error) {
      console.error('保存数据到本地存储失败:', error)
    }
  }

  // 动作 (Actions)
  /**
   * 获取学校列表（带缓存）
   * @param {boolean} forceRefresh - 是否强制刷新
   */
  const fetchSchoolList = async (forceRefresh = false) => {
    // 检查是否为学生用户，如果是，直接返回空数组，避免请求无权限的接口
    const isStudentUser = localStorage.getItem('student_token') !== null;
    if (isStudentUser) {
      console.log('学生用户，不获取学校列表数据');
      return [];
    }
  
    // 如果不强制刷新且缓存有效，直接返回缓存数据
    if (!forceRefresh && schoolList.value.length > 0 && isCacheValid() && schoolList.value) {
      console.log('使用缓存的学校列表数据')
      return schoolList.value
    }
  
    // 尝试从本地存储加载
    if (!forceRefresh && loadFromLocalStorage()) {
      return schoolList.value
    }
  
    try {
      loading.value = true
      error.value = null
      
      console.log('从服务器获取学校列表数据')
      const response = await getSchoolList()
      
      if (response.code === 200) {
        const data = response.data || []
        schoolList.value = data
        saveToLocalStorage(data)
        console.log('获取学校列表成功:', data.length, '条记录')
        return data
      } else {
        error.value = response.msg || '获取学校列表失败'
        console.error('获取学校列表失败:', error.value)
        return []
      }
    } catch (err) {
      error.value = '网络错误，获取学校列表失败'
      console.error('获取学校列表出错:', err)
      return []
    } finally {
      loading.value = false
    }
  }

  /**
   * 根据ID获取学校详细信息
   * @param {string} id - 学校ID
   */
  const fetchSchoolById = async (id) => {
    try {
      loading.value = true
      error.value = null
      
      const response = await getSchoolById({ id })
      
      if (response.code === 200) {
        currentSchool.value = response.data
        console.log('获取学校详情成功:', currentSchool.value)
        return currentSchool.value
      } else {
        error.value = response.msg || '获取学校详情失败'
        console.error('获取学校详情失败:', error.value)
        return null
      }
    } catch (err) {
      error.value = '网络错误，获取学校详情失败'
      console.error('获取学校详情出错:', err)
      return null
    } finally {
      loading.value = false
    }
  }

  /**
   * 添加学校
   * @param {Object} schoolData - 学校数据
   */
  const createSchool = async (schoolData) => {
    try {
      loading.value = true
      error.value = null
      
      const response = await addSchool(schoolData)
      
      if (response.code === 200) {
        // 添加成功后强制刷新列表
        await fetchSchoolList(true)
        console.log('添加学校成功')
        return true
      } else {
        error.value = response.msg || '添加学校失败'
        console.error('添加学校失败:', error.value)
        return false
      }
    } catch (err) {
      error.value = '网络错误，添加学校失败'
      console.error('添加学校出错:', err)
      return false
    } finally {
      loading.value = false
    }
  }

  /**
   * 更新学校信息
   * @param {Object} schoolData - 学校数据
   */
  const updateSchoolInfo = async (schoolData) => {
    try {
      loading.value = true
      error.value = null
      
      const response = await updateSchool(schoolData)
      
      if (response.code === 200) {
        // 更新成功后强制刷新列表
        await fetchSchoolList(true)
        console.log('更新学校成功')
        return true
      } else {
        error.value = response.msg || '更新学校失败'
        console.error('更新学校失败:', error.value)
        return false
      }
    } catch (err) {
      error.value = '网络错误，更新学校失败'
      console.error('更新学校出错:', err)
      return false
    } finally {
      loading.value = false
    }
  }

  /**
   * 删除学校
   * @param {string} id - 学校ID
   */
  const removeSchool = async (id) => {
    try {
      loading.value = true
      error.value = null
      
      const response = await deleteSchool({ id })
      
      if (response.code === 200) {
        // 删除成功后强制刷新列表
        await fetchSchoolList(true)
        console.log('删除学校成功')
        return true
      } else {
        error.value = response.msg || '删除学校失败'
        console.error('删除学校失败:', error.value)
        return false
      }
    } catch (err) {
      error.value = '网络错误，删除学校失败'
      console.error('删除学校出错:', err)
      return false
    } finally {
      loading.value = false
    }
  }

  /**
   * 根据名称搜索学校
   * @param {string} name - 学校名称
   */
  const searchSchools = async (name) => {
    try {
      loading.value = true
      error.value = null
      
      const response = await searchSchoolByName({ name })
      
      if (response.code === 200) {
        searchResults.value = response.data || []
        console.log('搜索学校成功:', searchResults.value)
        return searchResults.value
      } else {
        error.value = response.msg || '搜索学校失败'
        console.error('搜索学校失败:', error.value)
        return []
      }
    } catch (err) {
      error.value = '网络错误，搜索学校失败'
      console.error('搜索学校出错:', err)
      return []
    } finally {
      loading.value = false
    }
  }

  /**
   * 清空搜索结果
   */
  const clearSearchResults = () => {
    searchResults.value = []
  }

  /**
   * 清空错误信息
   */
  const clearError = () => {
    error.value = null
  }

  /**
   * 设置当前学校
   * @param {Object} school - 学校对象
   */
  const setCurrentSchool = (school) => {
    currentSchool.value = school
    try {
      if (school) {
        localStorage.setItem('currentSchool', JSON.stringify(school))
        console.log('当前学校已保存到本地存储:', school)
      } else {
        localStorage.removeItem('currentSchool')
        console.log('当前学校已从本地存储移除')
      }
    } catch (error) {
      console.error('保存当前学校到本地存储失败:', error)
    }
    applySchoolBranding()
  }

  const setCategoryList = (list) => {
    categoryList.value = Array.isArray(list) ? list : []
    try {
      localStorage.setItem('categoryList', JSON.stringify(categoryList.value))
    } catch (error) {
      console.error('保存分类列表失败:', error)
    }
  }

  const setCollegeList = (list) => {
    collegeList.value = Array.isArray(list) ? list : []
    try {
      localStorage.setItem('collegeList', JSON.stringify(collegeList.value))
    } catch (error) {
      console.error('保存学院列表失败:', error)
    }
  }

  const fetchSchoolCategories = async (schoolId) => {
    try {
      const res = await getTeacherSubjectCategories({ schoolId,allow:1 })
      if (res.code === 200 && res.data) {
        setCategoryList(res.data)
        return res.data
      }
      setCategoryList([])
      return []
    } catch (err) {
      setCategoryList([])
      return []
    }
  }

  const fetchSchoolColleges = async (schoolId) => {
    try {
      const res = await getTeacherCollegeList({ schoolId })
      if (res.code === 200 && res.data) {
        setCollegeList(res.data)
        return res.data
      }
      setCollegeList([])
      return []
    } catch (err) {
      setCollegeList([])
      return []
    }
  }

  /**
   * 清空当前学校
   */
  const clearCurrentSchool = () => {
    currentSchool.value = null
    try {
      localStorage.removeItem('currentSchool')
      console.log('当前学校已从本地存储清除')
    } catch (error) {
      console.error('清除本地存储中的当前学校失败:', error)
    }
    applySchoolBranding()
  }

  /**
   * 清除本地存储的缓存
   */
  const clearCache = () => {
    try {
      localStorage.removeItem('schoolList')
      localStorage.removeItem('schoolListFetchTime')
      localStorage.removeItem('currentSchool')
      lastFetchTime.value = null
      console.log('学校列表缓存已清除')
    } catch (error) {
      console.error('清除缓存失败:', error)
    }
    applySchoolBranding()
  }

  /**
   * 重置所有状态
   */
  const resetState = () => {
    schoolList.value = []
    currentSchool.value = null
    loading.value = false
    error.value = null
    searchResults.value = []
    clearCache()
  }

  // 初始化store
  initStore()

  // 返回状态和方法
  return {
    // 状态
    schoolList,
    currentSchool,
    loading,
    error,
    searchResults,
    
    // 计算属性
    getAllSchools,
    getCurrentSchool,
    isLoading,
    getError,
    getSearchResults,
    getCategoryList,
    getCollegeList,
    schoolCount,
    findSchoolById,
    
    // 方法
    fetchSchoolList,
    fetchSchoolById,
    fetchSchoolCategories,
    fetchSchoolColleges,
    createSchool,
    updateSchoolInfo,
    removeSchool,
    searchSchools,
    clearSearchResults,
    clearError,
    setCategoryList,
    setCollegeList,
    setCurrentSchool,
    clearCurrentSchool,
    resetState,
    clearCache,
    isCacheValid,
    loadFromLocalStorage,
    initStore
  }
})

// 默认导出（兼容性）
export default useSchoolStore
